import Rollbar from 'rollbar';
import type { App } from 'vue';
import { env } from './env';
import { getFullStorySessionUrl } from './fullstory';

/**
 * Browser error reporting to Rollbar's `halo-frontend` project (BPE-645).
 *
 * The token is the on/off switch: unless `VITE_ROLLBAR_TOKEN` is a real client
 * token nothing is created, no listeners are installed and nothing leaves the browser. Every
 * export below is safe to call either way.
 *
 * Reporting must never break the page, so init is wrapped in try/catch and
 * the SDK is bundled (not loaded from Rollbar's CDN): an ad blocker can only
 * block the POST, which the SDK drops silently.
 */

let rollbar: Rollbar | null = null;

// Rollbar access tokens are hex: 32 characters for older tokens, longer for
// tokens created since Rollbar's 2026 token change (halo-frontend's is 96).
// Checking the shape, not just presence, means a pipeline placeholder such as
// "off" (ADO cannot hold an empty variable) leaves reporting off instead of
// starting it with a bad token.
const TOKEN_PATTERN = /^[0-9a-f]{32,}$/i;

// 403 codes the app routes to a dedicated page (see services/api.ts). The
// rejection still propagates, but the user has already been handled.
const HANDLED_403_CODES = new Set(['USER_DEACTIVATED', 'ORG_DEACTIVATED', 'NO_ORGANIZATION']);

// Answered by nginx, the ingress or Front Door rather than ASP.NET (pod down,
// mid-rollout, upstream timeout), so the backend has no item for them.
const GATEWAY_STATUSES = new Set([502, 503, 504]);

interface IAxiosLikeError {
  isAxiosError: true;
  code?: string;
  response?: { status?: number; data?: { code?: string } };
}

function isAxiosLikeError(value: unknown): value is IAxiosLikeError {
  return typeof value === 'object' && value !== null && (value as { isAxiosError?: unknown }).isAxiosError === true;
}

/**
 * Whether an API failure is already accounted for elsewhere, so a Rollbar item
 * would only be noise:
 * - 5xx from ASP.NET: the backend reports its own error (`halo-backend`,
 *   BPE-808) and the shared trace ID links the two. Reporting here would
 *   duplicate it. Gateway 502/503/504 never reached ASP.NET, so they are kept.
 * - 401: the interceptor has already redirected to sign-in.
 * - Handled 403s: the user has been sent to the deactivated / no-org page.
 * - Cancelled requests: an intentional abort, not a failure.
 *
 * 4xx and network failures (no response) are reported. Follows `cause`, so
 * an axios error wrapped in another error keeps the rule.
 */
export function isAccountedForApiError(error: unknown, depth = 0): boolean {
  if (!isAxiosLikeError(error)) {
    const cause = (error as { cause?: unknown } | null)?.cause;
    return depth < 5 && cause !== undefined && isAccountedForApiError(cause, depth + 1);
  }
  if (error.code === 'ERR_CANCELED') return true;

  const status = error.response?.status;
  if (status === undefined) return false;
  if ((status >= 500 && !GATEWAY_STATUSES.has(status)) || status === 401) return true;
  return status === 403 && HANDLED_403_CODES.has(error.response?.data?.code ?? '');
}

/**
 * Rollbar `checkIgnore`. `args` holds the original error for both log calls
 * and unhandled rejections, which is where an uncaught axios error surfaces.
 */
export function shouldIgnore(_isUncaught: boolean, args: unknown[]): boolean {
  return args.some((arg) => isAccountedForApiError(arg));
}

/** Rollbar `transform`: link the item to the FullStory replay, when there is one. */
export function addFullStoryLink(data: Record<string, any>): void {
  const url = getFullStorySessionUrl();
  if (url) data.custom = { ...data.custom, fullstory_session_url: url };
}

/**
 * Initialize Rollbar and capture uncaught errors and unhandled rejections.
 * No-ops unless ROLLBAR_TOKEN is a Rollbar token. Call once at startup from main.ts,
 * before anything else that could throw.
 */
export function initRollbar(): void {
  if (!TOKEN_PATTERN.test(env.ROLLBAR_TOKEN) || rollbar) return;

  try {
    rollbar = new Rollbar({
      accessToken: env.ROLLBAR_TOKEN,
      environment: env.ROLLBAR_ENVIRONMENT,
      captureUncaught: true,
      captureUnhandledRejections: true,
      payload: {
        client: {
          javascript: {
            code_version: env.CODE_VERSION,
            source_map_enabled: true,
            guess_uncaught_frames: true,
          },
        },
      },

      // Merge `error.rollbarContext` into the item's custom data. The API
      // client uses it to attach the request's trace ID.
      addErrorContext: true,
      transform: addFullStoryLink,
      checkIgnore: shouldIgnore,

      // Noise control: the 100k/month quota is shared with other projects.
      ignoreBrowserExtensions: true,
      // Only errors with a frame from our own origin. This also drops errors
      // raised entirely inside third-party bundles such as the Apex simulation
      // component, which Apex owns. Entries are regexes, so escape the dots.
      hostSafeList: [window.location.hostname.replace(/\./g, '\\.')],
      ignoredMessages: [
        // Benign browser warning, not an application error.
        /ResizeObserver loop/,
        // Cross-origin script errors carry no message or stack.
        /^Script error\.?$/,
        // Stale-deploy chunk misses; main.ts already recovers by reloading.
        /Failed to fetch dynamically imported module/,
        /Importing a module script failed/,
        /error loading dynamically imported module/,
      ],
      itemsPerMinute: 10,
      maxItems: 50,

      // No sensitive data: no IP, no form input values, no request or
      // response bodies or headers in telemetry, and tokens, Okta's callback
      // params and search/email query params scrubbed wherever they appear
      // (telemetry records full request URLs). Person data is ID-only; see
      // setRollbarPerson.
      captureIp: 'anonymize',
      scrubTelemetryInputs: true,
      autoInstrument: {
        // Console telemetry would carry API response bodies, which
        // services/api.ts logs on every failed request.
        log: false,
        network: true,
        networkRequestHeaders: false,
        networkRequestBody: false,
        networkResponseHeaders: false,
        networkResponseBody: false,
      },
      scrubFields: [
        'authorization',
        'accessToken',
        'access_token',
        'idToken',
        'id_token',
        'refreshToken',
        'refresh_token',
        'token',
        'password',
        'code',
        'state',
        'email',
        'q',
        'search',
      ],
    });
  } catch (err) {
    rollbar = null;
    console.warn('[rollbar] init failed; error reporting is off', err);
  }
}

/**
 * Report Vue render, lifecycle, watcher and event-handler errors. Vue routes
 * these to `errorHandler` instead of `window.onerror`, so without this they
 * would never reach Rollbar. Still logs to the console, as Vue does by default.
 */
export function installRollbarVueHandler(app: App): void {
  if (!rollbar) return;

  app.config.errorHandler = (err, instance, info) => {
    reportError(err, {
      vue: {
        component: instance?.$options.name ?? instance?.$options.__name,
        info,
      },
    });
    console.error(err);
  };
}

/** Report an error that was caught but should still be counted. */
export function reportError(err: unknown, custom?: Record<string, unknown>): void {
  rollbar?.error(err instanceof Error ? err : String(err), custom);
}

/**
 * Attach the signed-in user to future items so Rollbar can count affected
 * users. Internal ID only — no email or name.
 */
export function setRollbarPerson(id: string): void {
  rollbar?.configure({ payload: { person: { id } } });
}

/** Stop attributing future items to the previous user on logout. */
export function clearRollbarPerson(): void {
  rollbar?.configure({ payload: { person: { id: null } } });
}

/** Test-only: drop the singleton so each test starts uninitialized. */
export function resetRollbarForTests(): void {
  rollbar = null;
}
